import { useEffect, useRef, useState, type FormEvent } from 'react';
import { setPortalIin } from '../../core/api/portal-service';
import { classifyIdentifier } from '../../core/auth/sign-in-identifier';
import { describeApiError } from '../../core/errors/describe-api-error';
import { describeFieldErrors } from '../../core/errors/describe-field-errors';
import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';
import { Alert } from '../../ui/alert';
import { Button } from '../../ui/button';
import { CheckIcon } from '../../ui/portal-icons';
import { TextField } from '../auth/text-field';

/**
 * The customer's IIN: a box while none is recorded, the number once one is.
 *
 * **Saving takes two steps, because the number cannot be changed afterwards.** The first shows the
 * twelve digits back as they will be stored and says so; only the second sends them. A transposed
 * pair that passes the check digit is rare, but a customer who made that mistake has no screen to
 * undo it on — only their adviser.
 *
 * The shape is checked here with the same reader the sign-in box uses, so a mistyped number is
 * corrected in the box. The server checks again and decides.
 */
export function IinField({ iin, onRecorded }: { iin: string | null; onRecorded: (iin: string) => void }) {
    const { t } = useTranslation();

    const [value, setValue] = useState('');
    const [confirming, setConfirming] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [fieldError, setFieldError] = useState<TranslationKey | undefined>(undefined);
    const [errorKey, setErrorKey] = useState<TranslationKey | null>(null);
    const confirmation = useRef<HTMLDivElement>(null);

    // The submit button the customer pressed is gone once the confirmation replaces it, so focus is
    // put on what replaced it rather than left on the page body.
    useEffect(() => {
        if (confirming !== null) {
            confirmation.current?.focus();
        }
    }, [confirming]);

    if (iin !== null) {
        return (
            <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{t('portal.iin')}</p>
                <p className="mt-1 font-mono text-lg font-semibold tracking-wider text-slate-900 dark:text-slate-100">
                    {iin}
                </p>

                {saved && (
                    <p className="mt-2 flex items-center gap-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-400">
                        <CheckIcon className="size-4" />
                        {t('portal.iin_saved')}
                    </p>
                )}

                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{t('portal.iin_recorded_hint')}</p>
            </div>
        );
    }

    function review(event: FormEvent) {
        event.preventDefault();
        setErrorKey(null);

        if (classifyIdentifier(value) !== 'iin') {
            setFieldError('auth.register.iin_invalid');
            return;
        }

        setFieldError(undefined);
        setConfirming(value.replace(/\D/g, ''));
    }

    async function save(digits: string) {
        setSaving(true);
        setErrorKey(null);

        try {
            await setPortalIin(digits);
            setSaved(true);
            onRecorded(digits);
        } catch (error) {
            const fields = describeFieldErrors(error);

            // Back to the box either way: whatever the server refused, the customer's next step is
            // the number, not the confirmation.
            setConfirming(null);
            setFieldError(fields['iin']);
            setErrorKey(fields['iin'] !== undefined ? null : describeApiError(error));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div>
            {confirming === null ? (
                <form onSubmit={review}>
                    <TextField
                        label={t('portal.iin')}
                        type="text"
                        value={value}
                        autoComplete="off"
                        inputMode="numeric"
                        maxLength={20}
                        error={fieldError}
                        hint={t('portal.iin_hint')}
                        onChange={(next) => {
                            setValue(next);
                            setFieldError(undefined);
                        }}
                    />

                    <Button type="submit" className="mt-3">
                        {t('portal.iin_save')}
                    </Button>
                </form>
            ) : (
                <div
                    ref={confirmation}
                    tabIndex={-1}
                    className="rounded-xl border border-amber-200 bg-amber-50 p-4 outline-none dark:border-amber-900 dark:bg-amber-950"
                >
                    <p className="text-sm font-bold text-amber-900 dark:text-amber-200">
                        {t('portal.iin_confirm_title')}
                    </p>
                    <p className="mt-2 font-mono text-xl font-semibold tracking-wider text-slate-900 dark:text-slate-100">
                        {confirming}
                    </p>
                    <p className="mt-2 text-sm text-amber-900 dark:text-amber-200">{t('portal.iin_confirm_hint')}</p>

                    <div className="mt-4 flex flex-wrap gap-2">
                        <Button type="button" disabled={saving} onClick={() => void save(confirming)}>
                            {saving ? t('portal.iin_saving') : t('portal.iin_confirm')}
                        </Button>
                        <Button type="button" variant="secondary" disabled={saving} onClick={() => setConfirming(null)}>
                            {t('portal.iin_edit')}
                        </Button>
                    </div>
                </div>
            )}

            {errorKey !== null && (
                <div className="mt-3">
                    <Alert>{t(errorKey)}</Alert>
                </div>
            )}
        </div>
    );
}
